import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Scale, ArrowRight } from "lucide-react";
import ExerciseDemo from "../components/ExerciseDemo";
import MuscleMap from "../components/MuscleMap";
import AdminDemoOverride from "../components/AdminDemoOverride";

export default function ExerciseDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [ex, setEx] = useState(null);

    const load = () => api.get(`/exercises/${id}`).then(r => setEx(r.data)).catch(() => setEx(null));

    useEffect(() => {
        load();
        // Jump to top whenever the user navigates to a new exercise — feels
        // wrong to land halfway down the page after tapping a variant card.
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (!ex) return <div data-testid="exercise-loading">Loading…</div>;

    return (
        <div className="space-y-8" data-testid="exercise-detail">
            <Link to="/exercises" className="label-eyebrow text-accent-red hover:underline" data-testid="exercise-back">← Back to library</Link>
            <div>
                <div className="label-eyebrow text-accent-red">{ex.muscle_group} · {ex.pattern}</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">{ex.primary && "★ "}{ex.name}</h1>
                {ex.description && <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{ex.description}</p>}
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-6">
                    {user?.role === "admin" && (
                        <AdminDemoOverride exercise={ex} onUpdate={load} />
                    )}
                    <ExerciseDemo
                        frames={ex.demo_frames || []}
                        youtubeId={ex.youtube_id}
                        youtubeId2={ex.youtube_id_2}
                        name={ex.name}
                    />

                    {ex.setup && (
                        <div className="tactical-card p-6">
                            <div className="label-eyebrow text-accent-red">Setup</div>
                            <ul className="mt-3 space-y-2 text-sm">
                                {ex.setup.map((s, i) => <li key={i} className="flex gap-2"><span className="text-accent-red">{i + 1}.</span>{s}</li>)}
                            </ul>
                        </div>
                    )}
                    {ex.execution && (
                        <div className="tactical-card p-6">
                            <div className="label-eyebrow text-accent-red">Execution</div>
                            <ul className="mt-3 space-y-2 text-sm">
                                {ex.execution.map((s, i) => <li key={i} className="flex gap-2"><span className="text-accent-red">{i + 1}.</span>{s}</li>)}
                            </ul>
                        </div>
                    )}
                    {ex.mistakes && (
                        <div className="tactical-card p-6">
                            <div className="label-eyebrow text-accent-red">Common mistakes</div>
                            <ul className="mt-3 space-y-2 text-sm">
                                {ex.mistakes.map((s, i) => <li key={i} className="flex gap-2"><span className="text-accent-red">✕</span>{s}</li>)}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <MuscleMap primary={ex.target_muscle || ex.muscle_group} pattern={ex.pattern} />
                    <div className="tactical-card p-6">
                        <div className="label-eyebrow text-accent-red">Programming</div>
                        <ul className="mt-3 space-y-2 text-sm">
                            <li>Sets: 2–3 working</li>
                            <li>Reps: 4–8</li>
                            <li>RIR: 1–2</li>
                            <li>Rest: 2–3 min</li>
                        </ul>
                    </div>
                    {ex.alternatives?.length > 0 && (
                        <div className="tactical-card p-6">
                            <div className="label-eyebrow text-accent-red">Alternatives</div>
                            <p className="mt-1 text-xs text-muted-foreground">Other lifts in the same pattern (<span className="font-bold uppercase text-foreground/80">{ex.pattern?.replaceAll("_", " ")}</span>) — see the Variants strip below for "use when…" guidance.</p>
                        </div>
                    )}
                </div>
            </div>

            {ex.alternatives?.length > 0 && (
                <div className="space-y-3" data-testid="variants-strip">
                    <div className="flex items-baseline justify-between">
                        <div>
                            <div className="label-eyebrow text-accent-red">Variants · same pattern</div>
                            <p className="text-xs text-muted-foreground">Decide which one fits today — based on goal, equipment, or how your joints feel.</p>
                        </div>
                        <div className="hidden text-xs text-muted-foreground sm:block">{ex.alternatives.length} option{ex.alternatives.length === 1 ? "" : "s"}</div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {ex.alternatives.map((a) => (
                            <Link
                                to={`/exercises/${a.id}`}
                                key={a.id}
                                data-testid={`variant-${a.id}`}
                                className="block-shadow tactical-card group p-4 transition hover:border-accent-red/60"
                            >
                                <div className="flex items-center justify-between label-eyebrow text-muted-foreground">
                                    <span>{a.muscle_group}</span>
                                    {a.unilateral && (
                                        <span className="flex items-center gap-1 border border-accent-red/60 px-1.5 py-0.5 text-[9px] tracking-wider text-accent-red">
                                            <Scale className="h-2.5 w-2.5" /> UNI
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 flex items-center gap-2">
                                    {a.primary && <span className="text-accent-red">★</span>}
                                    <h4 className="font-display text-base font-black uppercase leading-tight">{a.name}</h4>
                                </div>
                                {a.variant_tag && (
                                    <p className="mt-2 text-xs leading-snug text-muted-foreground" data-testid={`variant-tag-${a.id}`}>
                                        {a.variant_tag}
                                    </p>
                                )}
                                <div className="mt-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-accent-red opacity-0 transition-opacity group-hover:opacity-100">
                                    Open <ArrowRight className="h-3 w-3" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
